import { html, LitElement } from 'lit';
import { customElement } from 'lit/decorators/custom-element.js';
import { property } from 'lit/decorators/property.js';
import { state } from 'lit/decorators/state.js';
// @ts-ignore
import * as InfoCircle from '@patternfly/elements/pf-icon/icons/fas/info-circle.js';
import { bound } from '@patternfly/pfe-core/decorators.js';
import { ScreenSizeController } from '@rhds/elements/lib/ScreenSizeController.js';
import '@rhds/elements/rh-cta/rh-cta.js';
import '@rhds/elements/rh-tooltip/rh-tooltip.js';
import type {
  ClientParams, OidcManager
} from '@rhitm-incubator/oidc-manager';
import { oidcManagerResolved } from '../../lib/oidcManagerResolved.js';
import styles from './rhpt-login-button.css';

@customElement('rhpt-login-button')
export class RhptLoginButton extends LitElement {
  static styles = [styles];

  #screenSizeController = new ScreenSizeController(this);

  /*
   * This is a state property that will be used to determine if the user is authenticated anywhere on the page.
   * It is not dependent on the client_id or redirect_uri.
   *
   * This is what determines what text is displayed in the button / link.
   */
  @state()
    authenticated = false;

  /*
   * These are properties that are used to configure the SSO service.
   * client_id is the client_id of the application that is being used to authenticate the user.
   * authority is the URL of the SSO server that the client_id is registered with.
   */
  @property({ attribute: 'client-id' })
    client_id?: string;

  @property()
    authority?: string;

  /*
   * This is the redirect_uri that the user will be sent to after they have authenticated.
   */
  @property({ attribute: 'redirect-uri' })
    redirect_uri?: string;

  connectedCallback(): void {
    super.connectedCallback();
    this.#init();
  }

  /**
   * Initializes the component with OIDC Manager.
   */
  async #init() {
    // wait until the OIDC Manager singleton is ready
    await oidcManagerResolved;
    // see if there are any valid client users already
    this._checkAuthentication();
    // listen for future messages from OIDC Manager
    this.#registerEventListener();
  }

  /*
   * This method is used to check if the user is authenticated.
   * It is called when the oidcManager sends a message.
   * If the user is authenticated on any client on the page, the authenticated property is set to true.
   */
  @bound
  _checkAuthentication() {
    this.authenticated = window.oidcManager.getValidClientUsers().length > 0;
  }

  /*
   * This method is used to login the user.
   * It is called when the login button is clicked.
   * It calls the login method on the SSO service client.
   */
  #login() {
    if (!window.oidcManager || !this.client_id || !this.authority || !this.redirect_uri) {
      console.warn(`The oidcManager is not loaded or the client_id, authority, or redirect_uri is not set.`);
      return;
    }
    const client = window.oidcManager.createClient({
      client_id: this.client_id,
      authority: this.authority,
      redirect_uri: this.redirect_uri,
      force_redirect: true,
    });
    client.login();
  }

  /*
   * This method is used to register an event listener on the oidcManager.
   * It is called when the oidcManager is loaded.
   */
  #registerEventListener() {
    window.oidcManager.addEventListener('message', this._checkAuthentication);
  }

  disconnectedCallback(): void {
    // eslint-disable-next-line wc/guard-super-call
    super.disconnectedCallback();
    window.removeEventListener('message', this._checkAuthentication);
  }

  render() {
    if (!this.redirect_uri) {
      return this.renderNoRedirectUri();
    }
    return html`
      <div part="base">
        ${this.renderTooltip()}
        ${this.authenticated ? this.renderAuthenticated() : this.renderAnonymous()}
      </div>
    `;
  }

  /**
   * If the component has no redirect uri then we shouldn't display it at all.
   */
  renderNoRedirectUri() {
    console.warn(`The rhpt-login-button component requires a redirect-uri attribute to be set.`);
    return html``;
  }

  renderTooltip() {
    const tooltipPosition = this.#screenSizeController.matches.has('sm') ? 'left' : 'top';
    return html`
      <rh-tooltip part="tooltip" position=${tooltipPosition}>
        ${InfoCircle.default.cloneNode(true)}
        ${this.authenticated ? html`<slot slot="content" name="authenticated-tooltip"></slot>` : html`<slot slot="content" name="anonymous-tooltip"></slot>`}
      </rh-tooltip>
    `;
  }

  renderAnonymous() {
    // rh-cta that when clicked calls oidcmanager login
    // with a custom redirect path to the my trials page
    return html`
      <rh-cta part="cta" variant="secondary" @click="${() => this.#login()}">
        <button><slot name="anonymous-text"></slot></button>
      </rh-cta>
    `;
  }

  renderAuthenticated() {
    // A normal CTA that links to the my trials page
    return html`
      <rh-cta part="cta" variant="secondary">
        <a href="${this.redirect_uri!}"><slot name="authenticated-text"></slot></a>
      </rh-cta>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'rhpt-login-button': RhptLoginButton;
  }
  interface Window {
    /**
     * oidcManager provides SSO client communication for a consistent user experience.
     */
    oidcManager: OidcManager;
    drupalSettings?: {
      rh_oidc_manager: {
        oidc_settings: ClientParams;
      };
    };
  }
}
